import { getRouteApi } from "@tanstack/react-router";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { useSTPPlans, useStpStats } from "creso-core/hooks";
import {
  cn,
  DataTable,
  DataTablePagination,
  ScrollArea,
  ScrollBar,
  SearchInput,
  V2Loader,
} from "web-design-system";
import { useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { createStpColumns, NoData, StpStats } from "web-core";
import { StpRowActions } from "./plan-actions";

export function STPComponent() {
  const route = getRouteApi("/(auth)/_headed/clients/$id");
  const { client } = route.useLoaderData();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 500);

  const { data, isError, isLoading } = useSTPPlans(
    client?.investment_account ?? "",
    pagination.pageIndex + 1,
    pagination.pageSize,
    debouncedSearch
  );

  const { data: stats } = useStpStats(client?.investment_account || "");

  const columns = useMemo(
    () =>
      createStpColumns({
        renderActions: (plan) => <StpRowActions plan={plan} client={client} />,
      }),
    [client]
  );

  const table = useReactTable({
    data: data?.results || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
    manualPagination: true,
    getPaginationRowModel: getPaginationRowModel(),
    rowCount: data?.count,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  return (
    <V2Loader isLoading={isLoading} className="flex-1 gap-4">
      {isError || !data || (data?.results.length === 0 && !debouncedSearch) ? (
        <NoData
          description="Get them started. Investment Plans populate here."
          title="No STPs Yet"
        />
      ) : (
        <>
          <StpStats stats={stats} />
          <div className="w-70">
            <SearchInput
              placeholder="Search by scheme name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-col flex-1 w-full overflow-hidden border border-border rounded-lg">
            <ScrollArea className="flex-1 flex flex-col">
              <DataTable
                className={cn(
                  table.getFilteredRowModel().rows.length === 0 && "flex-1",
                  "border-0 flex-1"
                )}
                pagination={false}
                table={table}
              />

              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            <DataTablePagination
              table={table}
              className="sticky bottom-0 z-20"
            />
          </div>
        </>
      )}
    </V2Loader>
  );
}
